import React, { useState } from "react";
import PageShell from "../components/PageShell";
import { Mail, MapPin, Phone, Instagram, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      toast({ title: "Message sent", description: "Check your email for a confirmation." });
      setDone(true);
    } catch {
      toast({ title: "Could not send message", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell kicker="Contact" title="Let's put your story on a shirt" subtitle="Start with a design, a verse, or just a rough idea and a date you need it by. We reply to every message within one business day.">
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-4">
          <InfoCard icon={Mail} title="Email" value="designmystoryus@gmail.com" href="mailto:designmystoryus@gmail.com" />
          <InfoCard icon={Instagram} title="Instagram" value="@design.my.story" href="https://instagram.com/design.my.story" />
          <InfoCard icon={MapPin} title="Ships from" value="United States · nationwide delivery" />
          <InfoCard icon={Phone} title="Reply time" value="Within 1 business day" />
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-10">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center"><Mail className="w-7 h-7 text-[color:var(--dms-navy)]" /></div>
              <h2 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-6">Message received!</h2>
              <p className="mt-3 text-[color:var(--dms-navy)]/70">We sent a confirmation to {form.email}. We'll reply within one business day.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form">
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Send us a message</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name" required><input required data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
                <Field label="Email" required><input required type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
                <Field label="Phone" className="sm:col-span-2"><input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
                <Field label="Message" required className="sm:col-span-2"><textarea required rows={5} data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} placeholder="Tell us the idea, the verse or the deadline…" /></Field>
              </div>
              <div className="mt-6 flex items-center justify-end">
                <button type="submit" disabled={submitting} className="btn-peach" data-testid="contact-submit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}

const inputCls = "w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)] transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}{required && <span className="text-[color:var(--dms-peach)]"> *</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function InfoCard({ icon: Icon, title, value, href }) {
  const inner = (
    <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6 hover-lift">
      <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center"><Icon className="w-4 h-4 text-[color:var(--dms-navy)]" /></div>
      <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold mt-4">{title}</p>
      <p className="text-[color:var(--dms-navy)] font-serif text-lg mt-1">{value}</p>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}
